'use client';

import React from 'react';
import { LucideIcon, LucideProps } from 'lucide-react';
import IsClientCds from '@/components/cds/is-client-cds';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * Represents the type for the IconClientRendererCds component.
 * This type can either be a LucideIcon or a React forward reference exotic component
 * with properties excluding 'ref' from LucideProps and including React reference attributes for an SVG element.
 *
 * @typedef {LucideIcon | React.ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>>} IconClientRendererCdsType
 */
export type IconClientRendererCdsType =
  | LucideIcon
  | React.ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>>;

/**
 * Props for the IconClientRendererCds component.
 *
 * @property {IconClientRendererCdsType} Icon - The icon to be rendered.
 * @property {Omit<LucideProps, 'ref'>} LucideProps - Additional properties for the icon, excluding the 'ref' property.
 * @property {React.RefAttributes<SVGSVGElement>} ref - Ref attribute for the SVG element.
 */
export type IconClientRendererCdsProps = {
  icon: IconClientRendererCdsType;
} & Omit<LucideProps, 'ref'> &
  React.RefAttributes<SVGSVGElement>;

export function IconClientRendererCds({
  icon: Icon,
  ...props
}: Readonly<IconClientRendererCdsProps>) {
  return (
    <IsClientCds fallback={<Skeleton className={cn('h-4 w-4', props.className)} />}>
      <Icon {...props} />
    </IsClientCds>
  );
}
